<script>
  import ButtonLabel from "#lib/ButtonLabel.svelte";
  import { render_micro_markdown, trim_whitespaces, hash_string } from "#lib/util.js";
  import { slide } from 'svelte/transition';

  let {
    announcement = '',
    announcement_enabled = true,
  } = $props();

  const ANNOUNCEMENT_STORAGE_KEY = 'dismissed_announcement';
  const DISMISSAL_DURATION_MS = 3 * 30 * 24 * 60 * 60 * 1000; // 3 months in milliseconds

  let show_announcement = $state(false);

  /**
   * Check if the announcement should be shown based on localStorage state
   */
  function check_announcement_visibility() {
    if (!announcement_enabled || !announcement?.trim() || typeof window === 'undefined') {
      show_announcement = false;
      return;
    }

    try {
      const stored = localStorage.getItem(ANNOUNCEMENT_STORAGE_KEY);
      if (!stored) {
        show_announcement = true;
        return;
      }

      const { announcement_hash, dismissed_at } = JSON.parse(stored);
      const current_hash = hash_string(announcement);
      const time_since_dismissal = Date.now() - dismissed_at;

      show_announcement = announcement_hash !== current_hash || time_since_dismissal > DISMISSAL_DURATION_MS;
    } catch (error) {
      console.error('Error checking announcement visibility:', error);
      show_announcement = true;
    }
  }

  /**
   * Close the announcement and store the dismissal in localStorage
   */
  function close_announcement() {
    show_announcement = false;
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(ANNOUNCEMENT_STORAGE_KEY, JSON.stringify({
        announcement_hash: hash_string(announcement),
        dismissed_at: Date.now()
      }));
    } catch (error) {
      console.error('Error saving announcement dismissal:', error);
    }
  }

  // Check announcement visibility when the announcement text or enabled state changes
  $effect(() => {
    check_announcement_visibility();
  });

  const MAX_LINK_LABEL_LENGTH_FOR_NOWRAP = 30;

  /**
   * Check if links should not wrap based on max link label length
   * Returns true if announcement has links and the longest label is < 30 chars
   */
  let should_prevent_link_wrap = $derived.by(() => {
    if (!announcement) return false;
    // Match markdown links: [label](url)
    const link_regex = /\[([^\]]+)\]\([^)]+\)/g;
    const matches = [...announcement.matchAll(link_regex)];
    if (matches.length === 0) return false;
    const max_label_length = Math.max(...matches.map(m => m[1].length));
    return max_label_length < MAX_LINK_LABEL_LENGTH_FOR_NOWRAP;
  });
</script>

{#if show_announcement && announcement}
  <div class="announcement" transition:slide>
    <p class="text-control leading-trim" class:no-link-wrap={should_prevent_link_wrap}>{@html render_micro_markdown(trim_whitespaces(announcement))}</p>
    <button onclick={close_announcement} title="Close announcement">
      <ButtonLabel variant='brand flush text-compact' start_icon='close' />
    </button>
  </div>
{/if}

<style lang="postcss">
  .announcement {
    grid-area: announcement;
    background-color: var(--color-emphasis-background);
    --color-foreground: var(--color-emphasis-foreground);
    min-height: 44px;
    /* Match DynamicIsland */
    padding-inline: var(--page-inline-spacing);
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: var(--size-3);
    p {
      text-align: center;
      margin-inline: auto;
      padding-block: var(--size-5);
      /* Offset by button width to keep text visually centered */
      padding-inline-start: var(--input-min-height);
      :global(a) {
        text-decoration: underline;
        text-underline-offset: 0.3em;
      }
      &.no-link-wrap :global(a) {
        white-space: nowrap;
      }
    }
  }
</style>

